import { Close } from "@mui/icons-material";
import { Box as MuiBox, Button, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { SidePanelCloseButton } from "components/window/sidePanel/SidePanel";

export interface PickerOption<T extends string> {
    value: T;
    label: string;
    description?: string;
}

interface SettingsPickerViewProps<T extends string> {
    title: string;
    options: PickerOption<T>[];
    initialValue: T;
    onCancel: () => void;
    onDone: (value: T) => void;
    testIdPrefix?: string;
}

export function SettingsPickerView<T extends string>({
    title,
    options,
    initialValue,
    onCancel,
    onDone,
    testIdPrefix,
}: SettingsPickerViewProps<T>) {
    const { t } = useTranslation();
    const [staged, setStaged] = useState<T>(initialValue);

    return (
        <MuiBox
            display="flex"
            flexDirection="column"
            height="100%"
            data-testid={testIdPrefix ? `${testIdPrefix}-picker` : undefined}
        >
            {/* Header — matches SidePanel PanelHeader styling for visual consistency */}
            <MuiBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ padding: "12px 16px", minHeight: 48, flexShrink: 0 }}
            >
                <Typography variant="subtitle1" fontWeight={600}>
                    {title}
                </Typography>
                <SidePanelCloseButton
                    aria-label={t("close")}
                    onClick={onCancel}
                    size="small"
                    title={t("close")}
                    data-testid="settings-picker-close"
                >
                    <Close />
                </SidePanelCloseButton>
            </MuiBox>

            {/* Radio list */}
            <MuiBox flex={1} overflow="auto" sx={{ padding: "0 16px 16px" }}>
                <RadioGroup
                    value={staged}
                    onChange={(_e, value) => setStaged(value as T)}
                    sx={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}
                >
                    {options.map((option) => (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio color="primary" />}
                            data-testid={testIdPrefix ? `${testIdPrefix}-option-${option.value}` : undefined}
                            sx={{
                                margin: 0,
                                alignItems: "flex-start",
                                "& .MuiFormControlLabel-label": { width: "100%" },
                            }}
                            label={
                                <MuiBox display="flex" flexDirection="column" sx={{ paddingTop: "0.5rem" }}>
                                    <Typography variant="body2" sx={{ color: "text.primary" }}>
                                        {option.label}
                                    </Typography>
                                    {option.description && (
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{ whiteSpace: "normal" }}
                                        >
                                            {option.description}
                                        </Typography>
                                    )}
                                </MuiBox>
                            }
                        />
                    ))}
                </RadioGroup>
            </MuiBox>

            {/* Footer — Figma: Cancel (light gray) | Done (dark/black) */}
            <MuiBox
                display="flex"
                gap="12px"
                sx={{
                    padding: "12px 16px",
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "white",
                    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                    flexShrink: 0,
                }}
            >
                <Button
                    variant="contained"
                    disableElevation
                    onClick={onCancel}
                    data-testid="settings-picker-cancel"
                    sx={{
                        flex: 1,
                        bgcolor: "#F4F4F5",
                        color: "text.primary",
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "0.9375rem",
                        py: "10px",
                        "&:hover": { bgcolor: "#E4E4E7" },
                    }}
                >
                    {t("cancel")}
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={() => onDone(staged)}
                    data-testid="settings-picker-done"
                    sx={{
                        flex: 1,
                        bgcolor: "#18181B",
                        color: "white",
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.9375rem",
                        py: "10px",
                        "&:hover": { bgcolor: "#27272A" },
                    }}
                >
                    {t("done")}
                </Button>
            </MuiBox>
        </MuiBox>
    );
}
